// session.ts — browser-session (BFF cookie) client for the rivulet API,
// following the platform route contract in internal-docs
// standards/api/authentication.md §1 (served by the API via webby-bff-auth).
// Adapted from webby-bff-auth's docs/spa/session.ts reference client.
//
// Contract in one paragraph: every request carries the cookie
// (credentials:'include') AND the CSRF header (X-Requested-With). Login is a
// NAVIGATION to /oidc/login?return_to=<path>, never a fetch, and never with a
// full href. /oidc/userinfo is the cheap "is there a session" probe. Logout is
// POST /oidc/logout followed by a navigation to the returned end_session_url —
// that navigation is what ends the Authelia SSO session. A per-tab guard stops
// a 401→login→401 loop from spinning forever.
//
// Every helper here tolerates BOTH today's rivulet API (v0.12.x: userinfo
// answers `200 {authenticated:false}` when logged out, logout is a GET 302)
// AND the webby-bff-auth contract (userinfo `401 {error}` when logged out,
// logout is `POST → 200 {end_session_url}`), so this SPA can deploy before or
// after the API adoption.

export const CSRF_HEADER = 'X-Requested-With'
export const CSRF_VALUE = 'XMLHttpRequest'

/** The Authelia group the API requires for browser login (OIDC_REQUIRED_GROUP). */
export const REQUIRED_GROUP = 'rivulet-users'

const LOOP_KEY = 'bff.loginAttempts'
const MAX_ATTEMPTS = 2

/** What `GET /oidc/userinfo` tells us about the browser session. */
export interface SessionUser {
  email?: string
  display_name?: string
  /** Today's rivulet userinfo shape carries `username`; the contract does not. */
  username?: string
  groups: string[]
  session?: { created_at: string; expires_at: string }
}

/** API origin: VITE_API_URL in prod, empty in dev so the Vite proxy handles it. */
export function apiBase(): string {
  return (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')
}

/** Headers every request must carry. */
export function authHeaders(init?: HeadersInit): Headers {
  const h = new Headers(init)
  h.set(CSRF_HEADER, CSRF_VALUE)
  return h
}

/** Fetch with the session contract applied (cookie + CSRF header). */
export async function sessionFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const r = await fetch(`${apiBase()}${path}`, {
    ...init,
    credentials: 'include',
    headers: authHeaders(init.headers),
  })
  if (r.ok) noteAuthenticated()
  return r
}

/**
 * Reduce a return target to a rooted same-origin PATH (+search+hash). Full
 * hrefs on this origin are reduced to their path; anything else becomes '/'.
 * The API applies the same reduction server-side; doing it here too keeps the
 * URL we hand out honest.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string {
  if (!raw) return '/'
  let candidate = raw
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith('//')) {
    // Absolute URL or protocol-relative: only keep it if it is this origin.
    try {
      const u = new URL(raw, location.origin)
      if (u.origin !== location.origin) return '/'
      candidate = u.pathname + u.search + u.hash
    } catch {
      return '/'
    }
  }
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/\\')) {
    return '/'
  }
  return candidate
}

export function currentPath(): string {
  return location.pathname + location.search + location.hash
}

/** Where to send the browser to log in. `returnTo` is a PATH, never an href. */
export function loginUrl(returnTo: string = currentPath()): string {
  const rt = sanitizeReturnTo(returnTo)
  return `${apiBase()}/oidc/login?return_to=${encodeURIComponent(rt)}`
}

/** Number of automatic login bounces this tab has attempted. */
export function loginAttempts(): number {
  try {
    return Number(sessionStorage.getItem(LOOP_KEY) ?? '0') || 0
  } catch {
    return 0
  }
}

/** True once the per-tab guard has tripped (automatic bounces stop). */
export function loginLoopTripped(): boolean {
  return loginAttempts() >= MAX_ATTEMPTS
}

/**
 * Navigate to login. Returns false (and does nothing) when the per-tab guard
 * has tripped — the caller should render "sign-in failed" instead of retrying.
 * `force` bypasses the guard for an explicit sign-in button.
 */
export function redirectToLogin(returnTo?: string, force = false): boolean {
  const attempts = loginAttempts()
  if (!force && attempts >= MAX_ATTEMPTS) return false
  try {
    sessionStorage.setItem(LOOP_KEY, String(attempts + 1))
  } catch {
    /* private mode */
  }
  location.href = loginUrl(returnTo)
  return true
}

/** Clear the loop guard; called on any authenticated response. */
export function noteAuthenticated(): void {
  try {
    sessionStorage.removeItem(LOOP_KEY)
  } catch {
    /* ignore */
  }
}

export class SessionUnavailable extends Error {
  constructor(public code: string) {
    super(code)
    this.name = 'SessionUnavailable'
  }
}

/**
 * The session probe. Resolves to the user, null when there is no session,
 * or throws SessionUnavailable on 503 (login is not serving — show a message,
 * do NOT redirect).
 *
 * Accepts both shapes:
 *   - contract:  200 {email, display_name, groups, session} | 401 {error} | 503 {error}
 *   - today:     200 {authenticated: true, username, email, groups} | 200 {authenticated: false}
 */
export async function whoami(): Promise<SessionUser | null> {
  const r = await sessionFetch('/oidc/userinfo')
  if (r.status === 401) return null
  if (r.status === 503) {
    const body = (await r.json().catch(() => ({}))) as { error?: string }
    throw new SessionUnavailable(body?.error ?? 'oidc_unavailable')
  }
  if (!r.ok) throw new Error(`userinfo: ${r.status}`)

  const data = (await r.json()) as Record<string, unknown>
  // Today's API: an explicit `authenticated: false` means logged out.
  if (data.authenticated === false) return null
  // Contract: a 200 with no email is not a session we can use.
  if (data.authenticated !== true && typeof data.email !== 'string') return null

  return {
    email: typeof data.email === 'string' ? data.email : undefined,
    display_name: typeof data.display_name === 'string' ? data.display_name : undefined,
    username: typeof data.username === 'string' ? data.username : undefined,
    groups: Array.isArray(data.groups) ? (data.groups as string[]) : [],
    session:
      data.session && typeof data.session === 'object'
        ? (data.session as SessionUser['session'])
        : undefined,
  }
}

/** Read ?login_error=<code> once and strip it from the URL. */
export function takeLoginError(): string | null {
  const u = new URL(location.href)
  const code = u.searchParams.get('login_error')
  if (!code) return null
  u.searchParams.delete('login_error')
  history.replaceState(history.state, '', u.toString())
  return code
}

export const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  state_mismatch: 'The sign-in attempt expired or was tampered with. Please try again.',
  exchange_failed: 'The identity provider rejected the sign-in. Please try again.',
  missing_required_claims: 'Your account did not release an email address. Contact the operator.',
  forbidden_group: `Your account is not in the ${REQUIRED_GROUP} group, so it cannot use Rivulet. Ask the operator to add you.`,
  oidc_unavailable: 'Sign-in is temporarily unavailable. Please try again in a minute.',
  oidc_disabled: 'Browser sign-in is not enabled on this server.',
  user_resolve_failed: 'Your account could not be set up. Contact the operator.',
  internal_error: 'Something went wrong signing you in. Please try again.',
  login_loop: 'Sign-in keeps sending you back here without a session. Try again, or contact the operator if it persists.',
}

export function loginErrorMessage(code: string): string {
  return LOGIN_ERROR_MESSAGES[code] ?? `Sign-in failed (${code}). Please try again.`
}

/**
 * End the session. Tries the contract first — `POST /oidc/logout` with the
 * CSRF header, `200 {end_session_url}` — and navigates to that URL so the
 * Authelia SSO session is torn down too. Against today's API the POST is
 * answered 404/405 (the route is GET-only) or with no JSON body; in that case
 * we fall back to navigating `GET /oidc/logout`, which is today's behaviour.
 * Returns the URL that was navigated to.
 */
export async function endSession(): Promise<string> {
  const fallback = `${apiBase()}/oidc/logout`
  let target = fallback
  try {
    const r = await sessionFetch('/oidc/logout', { method: 'POST' })
    if (r.ok) {
      const body = (await r.json().catch(() => null)) as { end_session_url?: unknown } | null
      if (body && typeof body.end_session_url === 'string' && body.end_session_url) {
        target = body.end_session_url
      }
    }
  } catch (err) {
    console.warn('POST /oidc/logout failed; falling back to GET /oidc/logout', err)
  }
  noteAuthenticated()
  location.href = target
  return target
}
